import React, { useState } from 'react';
import { Handshake, CheckCircle, Users, TrendingUp, Globe, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import ConsentDialog from '../components/ConsentDialog';

const Partnerships = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    partnershipType: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  const benefits = [
    {
      icon: Users,
      title: 'Collaborative Growth',
      description: 'Work together to deliver exceptional value to clients and expand market reach'
    },
    {
      icon: TrendingUp,
      title: 'Revenue Opportunities',
      description: 'Access new revenue streams through joint solutions and referral programs'
    },
    {
      icon: Globe,
      title: 'Global Network',
      description: 'Connect with our worldwide network of technology partners and clients'
    },
    {
      icon: CheckCircle,
      title: 'Technical Support',
      description: 'Receive dedicated technical and business support from our team'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePartnershipTypeChange = (value) => {
    setFormData(prev => ({ ...prev, partnershipType: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConsent(true);
  };

  const submitForm = async () => {
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/partnership`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          partnershipType: '',
          description: ''
        });
        toast({
          title: 'Partnership Request Submitted!',
          description: 'We\'ll review your application and get back to you within 48 hours.',
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting partnership form:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit partnership request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      setShowConsent(false);
    }
  };

  return (
    <div className="min-h-screen">
      <ConsentDialog
        open={showConsent}
        onOpenChange={setShowConsent}
        onConsent={submitForm}
        isSubmitting={isSubmitting}
      />
      <SEO
        title="Partnerships — Technology & Reseller Programs | NIVA Digital AI"
        description="Join NIVA Digital's partner network. Explore technology partnerships, referral programs, and reseller opportunities for infrastructure solutions."
        keywords="NIVA partnerships, technology partner program, reseller partnerships, referral partners, partner network"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/partnerships`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-red-50">
        <div className="container mx-auto max-w-6xl text-center">
          <div className="inline-flex items-center space-x-2 bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Handshake size={20} />
            <span>Partner with NIVA Digital</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
            Collaborate, Innovate
            <span className="block text-red-600 mt-2">and Transform</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Join forces with NIVA Digital to deliver exceptional infrastructure solutions to clients worldwide
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Partnership Benefits
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Unlock new opportunities and grow your business with NIVA Digital
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 text-center">
                  <CardContent className="pt-8">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-red-600" size={32} />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnership Types */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Partnership Opportunities
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Technology Partners</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Integrate your technology solutions with our infrastructure services to deliver comprehensive solutions to clients.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">Co-marketing opportunities</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">Technical integration support</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">Joint solution development</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-red-600">
              <CardHeader>
                <CardTitle className="text-2xl">Referral Partners</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Refer clients to NIVA Digital and earn competitive commissions while providing value to your network.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">Attractive commission structure</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">Dedicated partner support</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">Marketing materials provided</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-2xl">Reseller Partners</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Resell NIVA Digital services under your brand and expand your service portfolio.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">White-label options available</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">Flexible pricing models</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="text-green-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-600">Sales enablement training</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partnership Application Form */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Apply for Partnership
            </h2>
            <p className="text-xl text-gray-600">
              Fill out the form below and we'll get back to you within 48 hours
            </p>
          </div>

          <Card className="border-2">
            <CardContent className="pt-8">
              {isSubmitted ? (
                <div className="py-12 text-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-green-600" size={48} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Thank You!</h3>
                  <p className="text-lg text-gray-600 mb-6">
                    Your partnership request has been successfully submitted. We'll review your application and get back to you within 48 hours.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      placeholder="Your Company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contactName">Contact Name *</Label>
                    <Input
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="contact@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="partnershipType">Partnership Type *</Label>
                  <Select value={formData.partnershipType} onValueChange={handlePartnershipTypeChange} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select partnership type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technology">Technology Partner</SelectItem>
                      <SelectItem value="referral">Referral Partner</SelectItem>
                      <SelectItem value="reseller">Reseller Partner</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Tell Us About Your Partnership Interest *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    placeholder="Describe your company, what you do, and how you envision partnering with NIVA Digital..."
                    rows={6}
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-red-600 hover:bg-red-700 text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Partnership Request'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Partnerships;